import { useEffect, useRef, useState } from "react";

/**
 * PaperLight — "Paper & Light" WebGL ground for the homepage hero.
 *
 * One full-screen triangle, one fragment shader: procedural paper grain plus a
 * slow daylight sweep (≈40 s cycle) that warms the sand where the light pools,
 * and a faint warm bloom that trails the pointer by ~300 ms. Renders at 2/3
 * resolution, 30 fps, and sleeps when off-screen, when the tab is hidden, or
 * under prefers-reduced-motion (one still frame). No WebGL → renders nothing,
 * so the section's plain `bg-sand` shows exactly as before.
 *
 * Founders can also switch it off or dial it brighter / calmer from /admin
 * (GET /api/site/paper-light → {enabled, intensity 0–2}); the code flag below still wins.
 *
 * TO TURN OFF IN CODE: set PAPER_LIGHT_ENABLED to false (or remove <PaperLight /> from Hero.jsx
 * and the <LitGround> wrappers on the Team / FAQ / Founding / Contact / Campaigns mastheads).
 */
export const PAPER_LIGHT_ENABLED = true;
const DEFAULT_SETTING = { enabled: true, intensity: 1 };

let settingPromise = null;
/** One fetch per page load, shared by every PaperLight on the page. */
export const fetchPaperLightSetting = (force = false) => {
  if (!settingPromise || force) {
    settingPromise = fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site/paper-light`)
      .then((r) => (r.ok ? r.json() : DEFAULT_SETTING))
      .then((d) => ({ enabled: d.enabled !== false, intensity: Number.isFinite(d.intensity) ? d.intensity : 1 }))
      .catch(() => DEFAULT_SETTING);
  }
  return settingPromise;
};

export const usePaperLightSetting = () => {
  const [setting, setSetting] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchPaperLightSetting().then((s) => { if (alive) setSetting(s); });
    return () => { alive = false; };
  }, []);
  return setting;
};

const SAND = [0xf9 / 255, 0xf6 / 255, 0xf0 / 255];
const FPS_INTERVAL = 1000 / 30;
const RES_SCALE = 0.66;

const VERT = `attribute vec2 a; void main(){ gl_Position = vec4(a, 0.0, 1.0); }`;

const FRAG = `
precision mediump float;
uniform vec2 u_res; uniform float u_time; uniform vec2 u_pointer; uniform float u_pointerStrength;
uniform vec3 u_base; uniform float u_intensity;
float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float vnoise(vec2 p){
  vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
}
void main(){
  vec2 p = gl_FragCoord.xy / u_res.y;
  float t = u_time / 40.0;
  float n = vnoise(p * 0.9 + vec2(t * 1.6, t * 0.5)) * 0.7 + vnoise(p * 2.1 - vec2(t * 0.9, t * 0.3)) * 0.3;
  float light = smoothstep(0.32, 0.88, n) * u_intensity;         /* admin dial: 0 still, 1 shipped look, 2 bright */
  vec3 col = u_base;
  col = mix(col, col * vec3(1.0, 0.988, 0.935), clamp(light * 0.55, 0.0, 1.0));   /* lit paper turns a touch warmer, never painted */
  col *= 1.0 + light * 0.018;                                    /* ≤2% luminance lift at intensity 1 */
  float d = distance(gl_FragCoord.xy, u_pointer) / (u_res.y * 0.55);
  float bloom = exp(-d * d * 3.0) * u_pointerStrength * u_intensity;
  col = mix(col, col * vec3(1.0, 0.99, 0.95), bloom * 0.5);
  col *= 1.0 + bloom * 0.03;
  col += (hash(gl_FragCoord.xy) - 0.5) * 0.022;                  /* paper grain */
  gl_FragColor = vec4(col, 1.0);
}`;

const compile = (gl, type, src) => {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  return gl.getShaderParameter(sh, gl.COMPILE_STATUS) ? sh : null;
};

export const PaperLight = ({ className = "", fade = false, intensity: forcedIntensity }) => {
  const ref = useRef(null);
  const intensityRef = useRef(1);
  const redrawRef = useRef(null);
  const setting = usePaperLightSetting();
  const enabled = PAPER_LIGHT_ENABLED && (forcedIntensity != null || setting?.enabled !== false);
  intensityRef.current = forcedIntensity ?? setting?.intensity ?? 1;

  useEffect(() => {
    const canvas = ref.current;
    if (!enabled || !canvas || (forcedIntensity == null && !setting)) return undefined;
    const gl = canvas.getContext("webgl", { antialias: false, depth: false, stencil: false, alpha: false, powerPreference: "low-power" });
    if (!gl) return undefined;
    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return undefined;
    const prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return undefined;
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const a = gl.getAttribLocation(prog, "a");
    gl.enableVertexAttribArray(a);
    gl.vertexAttribPointer(a, 2, gl.FLOAT, false, 0, 0);
    const u = (n) => gl.getUniformLocation(prog, n);
    const uRes = u("u_res"), uTime = u("u_time"), uPointer = u("u_pointer"), uStrength = u("u_pointerStrength"), uIntensity = u("u_intensity");
    gl.uniform3f(u("u_base"), SAND[0], SAND[1], SAND[2]);

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const state = { visible: true, last: 0, raf: 0, start: performance.now(), target: null, over: false, px: -9999, py: -9999, strength: 0 };

    const resize = () => {
      const w = Math.max(1, Math.round(canvas.clientWidth * RES_SCALE));
      const h = Math.max(1, Math.round(canvas.clientHeight * RES_SCALE));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; gl.viewport(0, 0, w, h); }
    };

    const draw = (now) => {
      resize();
      if (state.target) {
        const r = canvas.getBoundingClientRect();
        const tx = (state.target.x - r.left) * RES_SCALE, ty = (r.bottom - state.target.y) * RES_SCALE;
        if (state.px < -999) { state.px = tx; state.py = ty; }
        state.px += (tx - state.px) * 0.12; state.py += (ty - state.py) * 0.12;
      }
      state.strength += ((state.over ? 1 : 0) - state.strength) * 0.08;
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, reduced ? 7 : (now - state.start) / 1000);
      gl.uniform2f(uPointer, state.px, state.py);
      gl.uniform1f(uStrength, reduced ? 0 : state.strength);
      gl.uniform1f(uIntensity, intensityRef.current);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const loop = (now) => {
      state.raf = 0;
      if (!state.visible || document.hidden) return;
      if (now - state.last >= FPS_INTERVAL) { state.last = now; draw(now); }
      state.raf = requestAnimationFrame(loop);
    };
    const wake = () => { if (!state.raf && state.visible && !document.hidden && !reduced) state.raf = requestAnimationFrame(loop); };

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      state.over = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (state.over) state.target = { x: e.clientX, y: e.clientY };
    };
    const onLeave = () => { state.over = false; };
    const onLost = (e) => { e.preventDefault(); state.visible = false; };   /* stop drawing; section bg-sand shows through */
    canvas.addEventListener("webglcontextlost", onLost);
    const io = new IntersectionObserver(([en]) => { state.visible = en.isIntersecting; wake(); }, { threshold: 0 });
    io.observe(canvas);
    const ro = new ResizeObserver(() => { if (reduced) draw(performance.now()); });
    ro.observe(canvas);
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerleave", onLeave);
    window.addEventListener("blur", onLeave);

    redrawRef.current = () => draw(performance.now());
    if (reduced) draw(performance.now()); else wake();

    return () => {
      redrawRef.current = null;
      if (state.raf) cancelAnimationFrame(state.raf);
      io.disconnect(); ro.disconnect();
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("blur", onLeave);
      canvas.removeEventListener("webglcontextlost", onLost);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, setting === null]);

  useEffect(() => {
    intensityRef.current = forcedIntensity ?? setting?.intensity ?? 1;
    redrawRef.current?.();          /* still frame under reduced motion follows the dial too */
  }, [forcedIntensity, setting]);

  if (!enabled) return null;
  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      data-testid="paper-light-canvas"
      className={`pointer-events-none absolute inset-0 z-0 h-full w-full ${fade ? "paper-light-fade" : ""} ${className}`}
    />
  );
};

/** Full-bleed lit paper behind a page masthead; fades into the plain sand below so there is no seam. */
export const LitGround = ({ children, className = "" }) => (
  <div className={`relative overflow-hidden ${className}`} data-testid="lit-ground">
    <PaperLight fade />
    <div className="relative z-10">{children}</div>
  </div>
);
